import { ref } from "vue";
import { featureFlags } from "@/src/constants/sidebarConstants.js";
import { standardObjects } from "@/src/constants/objectConstants.js";
import { hiddenCRMObjects } from "@/src/constants/entityTypeConstants.js";
import { isStandardObjectsV2Enabled } from "@/src/utils/dynamicFormUtils.js";
import CRMService from "@/src/api/CRMService.js";
import DashboardService from "@/src/api/DashboardService.js";

export const useDynamicCRMObjects = () => {
  const dynamicCRMSections = ref([]);
  const allowedStandardObjects = ref([]);

  const fetchAllowedObjects = async () => {
    try {
      const response = await CRMService.fetchAllowedObjects();
      if (response.success) {
        allowedStandardObjects.value = response.standard_objects || [];
      }
    } catch (error) {
      console.error("Error fetching allowed objects:", error);
      allowedStandardObjects.value = [];
    }
  };

  const fetchDynamicCRMListObjects = async () => {
    try {
      const response = await CRMService.fetchDynamicCRMListObjects();
      if (response.success) {
        const objects = response.objects || [];
        const defaultSlugSet = new Set(standardObjects.map((o) => o.slug));
        const allowedSet = new Set(allowedStandardObjects.value);

        dynamicCRMSections.value = objects
          .filter((obj) => {
            if (hiddenCRMObjects.includes(obj.slug)) {
              return false;
            }

            if (!isStandardObjectsV2Enabled() && defaultSlugSet.has(obj.slug)) {
              return false;
            }

            // Filter out "customers" if not in allowed objects
            if (obj.slug === "customers" && !allowedSet.has("customers")) {
              return false;
            }

            if (obj.service && obj.service !== "crm") {
              return false;
            }

            return true;
          })
          .map((obj) => {
            return {
              ...obj,
            };
          });
      }
    } catch (error) {
      console.error("Error fetching dynamic CRM list objects:", error);
      dynamicCRMSections.value = [];
    }
  };

  return {
    dynamicCRMSections,
    allowedStandardObjects,
    fetchAllowedObjects,
    fetchDynamicCRMListObjects,
  };
};

const extractFeatureKeyFromPattern = (pattern) => {
  if (!pattern || typeof pattern !== "string") return null;
  const segments = pattern.split("/").filter(Boolean);
  return segments[segments.length - 1]?.replace(/_/g, "_");
};

const isFeatureDisabled = (section, features) => {
  if (
    section.id &&
    featureFlags[section.id] &&
    !features[featureFlags[section.id]]
  ) {
    return true;
  }

  if (section.matchPatterns && Array.isArray(section.matchPatterns)) {
    for (const pattern of section.matchPatterns) {
      const featureKey = extractFeatureKeyFromPattern(pattern);
      if (
        featureKey &&
        featureFlags[featureKey] &&
        !features[featureFlags[featureKey]]
      ) {
        return true;
      }
    }
  }

  return false;
};

export const useFeatureFlags = (baseOptions, hasPermission) => {
  // Store the original options to reset before each filter
  const originalOptions = ref([...baseOptions.value]);

  const fetchFeatures = async () => {
    try {
      // Reset to original options before filtering
      baseOptions.value = [...originalOptions.value];

      const { features } = await DashboardService.fetchEnabledFeatures();

      Object.entries(featureFlags).forEach(([key, flag]) => {
        features[flag]
          ? localStorage.setItem(key, "true")
          : localStorage.removeItem(key);
      });

      baseOptions.value = baseOptions.value.map((item) => {
        if (!item.sections) return item;
        return {
          ...item,
          sections: item.sections
            .filter((section) => {
              if (isFeatureDisabled(section, features)) {
                return false;
              }
              return hasPermission(section.permission);
            })
            .map((section) => {
              if (!section.sub_sections) return section;
              return {
                ...section,
                sub_sections: section.sub_sections.filter((sub) => {
                  return !isFeatureDisabled(sub, features);
                }),
              };
            }),
        };
      });

      baseOptions.value = baseOptions.value.filter((item) => {
        if (isFeatureDisabled(item, features)) return false;
        if (!hasPermission(item.permission)) return false;
        if (!item.sections) return true;
        return item.sections?.length > 0;
      });

      baseOptions.value = baseOptions.value.map((item) => {
        if (!item.sections) return item;

        return {
          ...item,
          sections: item.sections.map((section) => ({
            ...section,
            route: isFeatureDisabled(section, features) ? null : section.route,
          })),
        };
      });
    } catch (error) {
      console.error("Error fetching features:", error);
    }
  };

  return {
    fetchFeatures,
  };
};
